import React, { useState } from 'react';
import { ReebeloLanguage } from '@lambda/reebelo';
import { PimProductT } from '@lambda/apis/src/dynamoDb/types';
import { sortByStorage } from '@lambda/commons/sort';
import { invert } from 'lodash-es';

import * as ColorVariant from '../ColorVariant';
import Variant from '../Variant';
import i18n from '@/settings/i18n';
import {
  ConnectivitieSkus,
  OfferTree,
  SelectionT,
} from '@/lib/collections/catalogue/helpers';
import ColorSwatch from './ColorSwatch';

const t = i18n.init();

interface LaptopVariantsProps {
  product: PimProductT;
  filteredOfferTree: OfferTree;
  selection: SelectionT;
  language: ReebeloLanguage;
  colors: Record<string, string>;
  isUnfitProduct: boolean;
  setSuggestedVariant: React.Dispatch<React.SetStateAction<string>>;
  suggestedVariant: string;
}

export const filteredVariants = (variants: string[], _offerTree: OfferTree) =>
  variants?.length
    ? variants.filter((variant) =>
        _offerTree.orderedPath.some((path) =>
          path.split(',').find((p) => p === variant),
        ),
      )
    : [];

export default function LaptopProductVariants({
  product,
  selection,
  language,
  colors,
  isUnfitProduct,
  suggestedVariant,
  setSuggestedVariant,
  filteredOfferTree,
}: LaptopVariantsProps) {
  const [hoverColor, setHoverColor] = useState('');
  const colorsToCodeMapping = invert(colors);

  const storages = filteredVariants(product.storages, filteredOfferTree);
  const rams = filteredVariants(product.rams, filteredOfferTree);
  const cpus = filteredVariants(product.cpus, filteredOfferTree);
  const productColors = filteredVariants(
    Object.values(colors),
    filteredOfferTree,
  );
  const screenSizes = filteredVariants(product.screenSizes, filteredOfferTree);
  const chargers = filteredVariants(
    [...(product.chargers ?? []), 'None'],
    filteredOfferTree,
  );
  const touchbars = filteredVariants(product.touchbars, filteredOfferTree);
  const versions = filteredVariants(product.versions, filteredOfferTree);
  const connectivities = (product?.connectivities ?? []).filter(
    (connectivity) =>
      filteredOfferTree.orderedPath.some((path) =>
        path
          .split(',')
          .find((p) => p === ConnectivitieSkus[language][connectivity]),
      ),
  );

  const showScreenSizes =
    (product.brand === 'Apple' && product.category === 'Laptops') ||
    screenSizes.length > 1;

  return (
    <>
      {/* CPU */}
      <Variant.Group
        title={t`CPU`}
        psku={product.psku}
        className="grid grid-cols-2"
        id="e2e-product-cpu"
        suggestedVariant={suggestedVariant}
      >
        {cpus?.map((e) => (
          <Variant.Anchor
            disabled={isUnfitProduct}
            variant="cpu"
            setSuggestedVariant={setSuggestedVariant}
            key={e}
            className="relative px-5 sm:px-8"
            query={filteredOfferTree.getFrom(
              {
                cpu: e,
              },
              selection,
            )}
            active={selection.cpu === e}
          >
            {e}
          </Variant.Anchor>
        ))}
      </Variant.Group>
      {/* RAM  */}
      <Variant.Group
        title={t`Ram`}
        psku={product.psku}
        className="grid grid-cols-4"
        id="e2e-product-ram"
        suggestedVariant={suggestedVariant}
      >
        {rams.sort(sortByStorage).map((e) => (
          <Variant.Anchor
            disabled={isUnfitProduct}
            variant="ram"
            setSuggestedVariant={setSuggestedVariant}
            key={e}
            className="relative px-5 sm:px-8"
            query={filteredOfferTree.getFrom(
              {
                cpu: selection.cpu,
                ram: e,
              },
              selection,
              6,
            )}
            active={selection.ram === e}
          >
            {e}
          </Variant.Anchor>
        ))}
      </Variant.Group>
      {/* Storage */}
      <Variant.Group
        title={t`Storage`}
        psku={product.psku}
        className="grid grid-cols-3 xs:grid-cols-4"
        id="e2e-product-storage"
        suggestedVariant={suggestedVariant}
      >
        {storages.sort(sortByStorage).map((e) => (
          <Variant.Anchor
            disabled={isUnfitProduct}
            variant="storage"
            setSuggestedVariant={setSuggestedVariant}
            key={e}
            query={filteredOfferTree.getFrom(
              {
                cpu: selection.cpu,
                ram: selection.ram,
                storage: e,
              },
              selection,
            )}
            active={selection.storage === e}
          >
            {e}
          </Variant.Anchor>
        ))}
      </Variant.Group>
      {/* Color */}
      <ColorVariant.Group
        title={t`Color`}
        psku={product.psku}
        selectedColor={hoverColor !== '' ? hoverColor : selection.color}
        id="e2e-product-color"
        suggestedVariant={suggestedVariant}
      >
        {productColors.sort().map((e) => {
          // if color is not defined fall back to white color
          // if loading placeholder color is grey
          const currentColorCode = product.colorsInHex
            ? product.colorsInHex[colorsToCodeMapping[e]] || '#ffffff'
            : '#cccccc';
          const query = filteredOfferTree.getFrom(
            {
              cpu: selection.cpu,
              ram: selection.ram,
              storage: selection.storage,
              color: e,
            },
            selection,
          );

          return (
            <ColorVariant.Anchor
              disabled={isUnfitProduct}
              key={e}
              query={query}
              suggested={query?.suggested}
              currentColor={e}
              active={selection.color === e}
              onHover={(c) => setHoverColor(c)}
              onHoverOut={() => setHoverColor('')}
              setSuggestedVariant={setSuggestedVariant}
              variant="color"
            >
              <ColorSwatch
                color={currentColorCode}
                active={isUnfitProduct ? false : selection.color === e}
                outOfStock={query == null || isUnfitProduct}
                suggested={query?.suggested}
              />
            </ColorVariant.Anchor>
          );
        })}
      </ColorVariant.Group>

      {/* Screen Size */}
      {showScreenSizes && (
        <>
          <Variant.Group
            title={t`Screen Size`}
            psku={product.psku}
            className="grid grid-cols-2"
            id="e2e-product-screen-size"
            suggestedVariant={suggestedVariant}
          >
            {screenSizes?.map((e) => (
              <Variant.Anchor
                disabled={isUnfitProduct}
                variant="screen size"
                setSuggestedVariant={setSuggestedVariant}
                key={e}
                query={filteredOfferTree.getFrom(
                  {
                    cpu: selection.cpu,
                    ram: selection.ram,
                    storage: selection.storage,
                    color: selection.color,
                    screenSize: e,
                  },
                  selection,
                )}
                active={selection.screenSize === e}
              >
                {e}
              </Variant.Anchor>
            ))}
          </Variant.Group>
        </>
      )}

      {/* Charger  */}
      <Variant.Group
        title={t`Charger`}
        psku={product.psku}
        className="grid grid-cols-4"
        id="e2e-product-charger"
        suggestedVariant={suggestedVariant}
      >
        {chargers.map((e) => (
          <Variant.Anchor
            disabled={isUnfitProduct}
            variant="charger"
            setSuggestedVariant={setSuggestedVariant}
            key={e}
            query={filteredOfferTree.getFrom(
              {
                cpu: selection.cpu,
                ram: selection.ram,
                storage: selection.storage,
                color: selection.color,
                screenSize: selection.screenSize,
                charger: e,
              },
              selection,
            )}
            active={selection.charger === e}
          >
            {e}
          </Variant.Anchor>
        ))}
      </Variant.Group>

      {/* Touchbar */}
      <Variant.Group
        title={t`Touchbar`}
        psku={product.psku}
        className="grid grid-cols-4"
        id="e2e-product-touchbar"
        suggestedVariant={suggestedVariant}
      >
        {touchbars.map((e) => (
          <Variant.Anchor
            disabled={isUnfitProduct}
            variant="touchbar"
            setSuggestedVariant={setSuggestedVariant}
            key={e}
            query={filteredOfferTree.getFrom(
              {
                cpu: selection.cpu,
                ram: selection.ram,
                storage: selection.storage,
                color: selection.color,
                screenSize: selection.screenSize,
                charger: selection.charger,
                touchbar: e,
              },
              selection,
            )}
            active={selection.touchbar === e}
          >
            {e}
          </Variant.Anchor>
        ))}
      </Variant.Group>

      {/* Version  */}
      <Variant.Group
        title={t`Version`}
        psku={product.psku}
        className="grid grid-cols-4"
        id="e2e-product-version"
        suggestedVariant={suggestedVariant}
      >
        {versions.map((e) => (
          <Variant.Anchor
            disabled={isUnfitProduct}
            variant="version"
            setSuggestedVariant={setSuggestedVariant}
            key={e}
            query={filteredOfferTree.getFrom(
              {
                cpu: selection.cpu,
                ram: selection.ram,
                storage: selection.storage,
                color: selection.color,
                screenSize: selection.screenSize,
                charger: selection.charger,
                touchbar: selection.touchbar,
                version: e,
              },
              selection,
            )}
            active={selection.version === e}
          >
            {e}
          </Variant.Anchor>
        ))}
      </Variant.Group>

      {/* Connectivity */}
      <Variant.Group
        title={t`Connectivity`}
        psku={product.psku}
        className="grid grid-cols-2 xxs:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3"
        id="e2e-product-connectivity"
        suggestedVariant={suggestedVariant}
      >
        {connectivities
          .map((c) => ConnectivitieSkus[language][c])
          .map((e) => (
            <Variant.Anchor
              disabled={isUnfitProduct}
              variant="connectivity"
              setSuggestedVariant={setSuggestedVariant}
              key={e}
              className="relative px-5 sm:px-8"
              query={filteredOfferTree.getFrom(
                {
                  cpu: selection.cpu,
                  ram: selection.ram,
                  storage: selection.storage,
                  color: selection.color,
                  screenSize: selection.screenSize,
                  charger: selection.charger,
                  touchbar: selection.touchbar,
                  version: selection.version,
                  connectivity: e,
                },
                selection,
              )}
              active={selection.connectivity === e}
            >
              {t(e)}
            </Variant.Anchor>
          ))}
      </Variant.Group>
    </>
  );
}
